import {
  Alert, Autocomplete, Box, Chip, CircularProgress, Drawer, IconButton, MenuItem,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { GRIPP_FIELD_OPTIONS, GRIPP_FIELD_LABELS } from './offerteUi';

/**
 * Het eigenschappen-paneel rechts van het offerte-document: klant, namens,
 * contactpersoon, de verplichte Gripp-velden en de taal.
 *
 * Vanaf brede schermen een persistent paneel; daaronder een temporary Drawer.
 * ⚠ Nooit volledig verstopt: de container opent de drawer vanzelf zodra er
 * verplichte velden ontbreken (FF-REV-004 — verplichte velden wegstoppen gaf
 * eerder al verwarring), en de statusregel toont een teller.
 */
function EigenschappenInhoud({
  company, companyOpts, companyLoading, companySource, onCompanySearch, onCompanyChange,
  onRefreshCompanies, companyRefreshTerm, companyInputRef,
  employees, accountManager, onAccountManagerChange,
  contacts, contact, contactsLoading, onContactChange, onReloadContacts,
  draft, onDraftField, language, onLanguageChange,
  clientAgreement, englishGaps,
}) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>
        VOOR WIE &amp; NAMENS WIE
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 0 }} size="small" options={companyOpts} value={company}
          getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={companyLoading} filterOptions={(x) => x}
          onInputChange={(_e, val, reason) => { if (reason === 'input') onCompanySearch(val); }}
          onChange={(_e, val) => onCompanyChange(val)}
          renderInput={(params) => (
            <TextField {...params} inputRef={companyInputRef} label="Klant (Gripp-bedrijf)"
              placeholder="Zoek bedrijf in Gripp…"
              error={!company} helperText={!company ? 'Verplicht in Gripp' : ''} />
          )}
          noOptionsText="Typ om te zoeken — nieuw bedrijf in Gripp? Klik op ↻"
        />
        <Tooltip title="Bedrijven opnieuw uit Gripp halen — bv. na het aanmaken van een nieuwe relatie">
          <span>
            <IconButton size="small" disabled={companyLoading || companyRefreshTerm.length < 2}
              onClick={onRefreshCompanies}>
              {companyLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {companySource === 'spiegel' && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Gripp is even niet bereikbaar — deze klanten komen uit onze eigen kopie en
          kunnen achterlopen. Je kunt de offerte gewoon voorbereiden, maar hem pas
          naar Gripp sturen als de verbinding er weer is.
        </Alert>
      )}
      {clientAgreement && (
        <Alert severity="success" icon={<HandshakeIcon fontSize="inherit" />} sx={{ py: 0.25 }}>
          Prijsafspraken voor <b>{clientAgreement.company_name}</b> worden meegenomen.
        </Alert>
      )}
      <Autocomplete
        sx={{ minWidth: 0 }} size="small" options={employees} value={accountManager}
        getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(_e, val) => onAccountManagerChange(val)}
        renderInput={(params) => <TextField {...params} label="Namens (medewerker)" placeholder="Kies medewerker…" />}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 0 }} size="small" options={contacts} value={contact}
          getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_e, val) => onContactChange(val)}
          renderInput={(params) => <TextField {...params} label="Contactpersoon klant" placeholder={company ? 'Kies contactpersoon…' : 'Kies eerst een klant'} />}
          noOptionsText={company ? 'Geen contactpersonen gevonden — net aangemaakt in Gripp? Klik op ↻' : 'Kies eerst een klant'}
        />
        <Tooltip title="Contactpersonen opnieuw uit Gripp halen — bv. na het aanmaken van een nieuwe">
          <span>
            <IconButton size="small" disabled={!company?.id || contactsLoading} onClick={onReloadContacts}>
              {contactsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em', pt: 1 }}>
        GRIPP-VELDEN
      </Typography>
      {/* Verplichte Gripp-velden — de assistent vult ze voor; hier corrigeren. */}
      {Object.keys(GRIPP_FIELD_OPTIONS).map((f) => (
        <TextField key={f} select size="small" label={`${GRIPP_FIELD_LABELS[f]} (Gripp)`}
          value={GRIPP_FIELD_OPTIONS[f].includes(draft?.[f]) ? draft[f] : ''}
          onChange={(e) => onDraftField(f, e.target.value)}
          sx={{ minWidth: 0 }}
          error={!GRIPP_FIELD_OPTIONS[f].includes(draft?.[f])}
          helperText={!GRIPP_FIELD_OPTIONS[f].includes(draft?.[f]) ? 'Verplicht in Gripp' : ''}>
          {GRIPP_FIELD_OPTIONS[f].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      ))}
      {/* Omzetverdeling (Gripp Professional rapportage): zonder deze periode mist
          de offerte in de omzetrapportage — vandaar de harde waarschuwing. */}
      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em', pt: 1 }}>
        OMZETVERDELING (RAPPORTAGE)
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField size="small" type="month" label="Van" value={draft?.omzetStart || ''}
          onChange={(e) => onDraftField('omzetStart', e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 0 }}
          error={!draft?.omzetStart} />
        <TextField size="small" type="month" label="T/m" value={draft?.omzetEind || ''}
          onChange={(e) => onDraftField('omzetEind', e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 0 }}
          error={!draft?.omzetEind || (!!draft?.omzetStart && draft.omzetEind < draft.omzetStart)} />
      </Stack>
      {(!draft?.omzetStart || !draft?.omzetEind) && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Omzetverdeling niet ingevuld — deze offerte mist dan in de Gripp-rapportage.
        </Alert>
      )}
      {draft?.omzetStart && draft?.omzetEind && draft.omzetEind < draft.omzetStart && (
        <Alert severity="warning" sx={{ py: 0.25 }}>De einddatum ligt vóór de startdatum.</Alert>
      )}

      {/* Taal van de regels: bij EN zoekt de backend het Engelse Gripp-product op,
          anders blijft de regeltitel Nederlands (die titel ís de productnaam). */}
      <TextField select size="small" label="Taal offerteregels" value={language}
        onChange={(e) => onLanguageChange(e.target.value)}
        sx={{ minWidth: 0 }}
        helperText={draft?.language ? 'Handmatig gekozen' : 'Automatisch herkend'}>
        <MenuItem value="nl">Nederlands</MenuItem>
        <MenuItem value="en">Engels</MenuItem>
      </TextField>
      {language === 'en' && englishGaps?.length > 0 && (
        <Alert severity="info" sx={{ py: 0.25 }}>
          Voor {englishGaps.length} regel{englishGaps.length === 1 ? '' : 's'} vond ik geen
          Engels product in de Gripp-catalogus — de Engelse tekst staat nu áchter de gevonden productnaam als regeltitel.
          Voeg die producten in Gripp toe voor een volledig Engelse offerte:{' '}
          {englishGaps.map((g) => g.label).filter(Boolean).join(', ')}
        </Alert>
      )}
    </Stack>
  );
}

function PaneelKop({ missingCount, onClose }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#1f2937' }}>Eigenschappen</Typography>
      {missingCount > 0 && (
        <Chip size="small" label={`⚠ ${missingCount}`}
          sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#fef3c7', color: '#92400e' }} />
      )}
      <Box sx={{ flex: 1 }} />
      {onClose && (
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      )}
    </Stack>
  );
}

function OfferteEigenschappen({ variant, open, onClose, missingCount, ...inhoud }) {
  if (variant === 'drawer') {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: 360, maxWidth: '92vw', p: 2 } }}>
        <PaneelKop missingCount={missingCount} onClose={onClose} />
        <EigenschappenInhoud {...inhoud} />
      </Drawer>
    );
  }
  return (
    <Box sx={{ width: 340, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#fafbff', p: 2 }}>
      <PaneelKop missingCount={missingCount} />
      <EigenschappenInhoud {...inhoud} />
    </Box>
  );
}

export default OfferteEigenschappen;
